/**
 * Canonical SEO URL pattern for activity pages:
 * `/<lang>/<city>/<category-or-segment>/<slug>`
 *
 * - Bookable items use `categorySlug` (e.g. "quad", "excursions").
 * - Explore items use `segment` (e.g. "restaurants", "locations") since
 *   they have no category.
 * - Falls back to "place" when neither is available.
 */
export interface ActivityHrefInput {
  lang: string;
  citySlug?: string | null;
  categorySlug?: string | null;
  segment?: string | null;
  slug: string;
}

export function activityHrefParts(input: ActivityHrefInput): {
  to: "/$lang/$city/$category/$slug";
  params: { lang: string; city: string; category: string; slug: string };
} {
  const city = input.citySlug?.trim() || "morocco";
  const category =
    input.categorySlug?.trim() ||
    input.segment?.trim() ||
    "place";
  return {
    to: "/$lang/$city/$category/$slug",
    params: { lang: input.lang, city, category, slug: input.slug },
  };
}

/**
 * The category of the transfer routes. A route has no page of its own (owner
 * decision): it is booked on the transport hub, with the route preselected.
 */
export const TRANSPORT_CATEGORY_SLUG = "transport";

/** Whether an activity is a transfer route, by category or by template. */
export function isTransferActivity(a: {
  categorySlug?: string | null;
  category?: { slug?: string | null } | null;
  template?: string | null;
}): boolean {
  const cat = (a.categorySlug ?? a.category?.slug ?? "").trim().toLowerCase();
  return cat === TRANSPORT_CATEGORY_SLUG || a.template === "transfer";
}

/** The hub with one route preselected, as router parts. */
export function transportHubParts(lang: string, slug: string): {
  to: "/$lang/transport";
  params: { lang: string };
  search: { route: string };
} {
  return { to: "/$lang/transport", params: { lang }, search: { route: slug } };
}

/** The hub with one route preselected, as a path: `/<lang>/transport?route=<slug>`. */
export function transportHubHref(lang: string, slug: string): string {
  return `/${lang}/transport?route=${encodeURIComponent(slug)}`;
}

/**
 * The page an activity lives on. A transfer route has none, so it points at
 * the hub with the route preselected; everything else gets its canonical
 * 4-segment page.
 */
export function activityHrefString(input: ActivityHrefInput): string {
  if (isTransferActivity(input)) return transportHubHref(input.lang, input.slug);
  const p = activityHrefParts(input);
  return `/${p.params.lang}/${p.params.city}/${p.params.category}/${p.params.slug}`;
}

/**
 * Renamed activity slugs that Google still has indexed. A rename without an
 * entry here turns a ranking URL into a hard 404 (GSC 2026-07: "matsuri" was
 * earning 1.5k impressions/28d at position ~6 while 404ing). Route loaders
 * consult this map before falling through to notFound().
 */
export const LEGACY_ACTIVITY_SLUGS: Record<string, string> = {
  matsuri: "matsuri-rabat",
};

export function resolveLegacyActivitySlug(slug: string): string {
  return LEGACY_ACTIVITY_SLUGS[slug] ?? slug;
}
